/**
 * Site settings → Design system. Shows which design system the site's
 * content renders with, lets a collaborator agree to run an uploaded one on
 * their device, and lets the owner install a new release or go back to Duro.
 *
 * The editor's own UI stays Duro whatever is chosen here; this only changes
 * how the SITE's buttons, headings, cards and inputs render.
 */
import { useState } from "react";
import {
  Alert,
  Button,
  Callout,
  Drawer,
  Inline,
  Spinner,
  Stack,
  Text,
} from "@duro-app/ui";

import { browserDsDeps } from "../designSystem/browserDeps.js";
import {
  installDesignSystem,
  type DsDeps,
  type InstallResult,
} from "../designSystem/designSystemFlows.js";
import type { UseActiveDesignSystem } from "../designSystem/useActiveDesignSystem.js";

interface Props {
  readonly siteId: string;
  readonly isOwner: boolean;
  readonly designSystem: UseActiveDesignSystem;
  /** Tests inject fakes; the editor uses the real ones. */
  readonly deps?: (siteId: string) => DsDeps;
}

/** The native file picker, opened from a DS button (Duro has no file field). */
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.addEventListener("change", () => resolve(input.files?.[0] ?? null));
    input.addEventListener("cancel", () => resolve(null));
    input.click();
  });
}

const STAGE: Record<Exclude<InstallResult, { ok: true }>["stage"], string> = {
  compile: "It can't be used as uploaded",
  load: "It didn't load",
  conformance: "Its components don't render what the site needs",
  upload: "It couldn't be uploaded",
  activate: "It couldn't be activated",
};

export function DesignSystemTab({
  siteId,
  isOwner,
  designSystem,
  deps = browserDsDeps,
}: Props) {
  const { state, allow, reload } = designSystem;
  const [bundle, setBundle] = useState<File | null>(null);
  const [css, setCss] = useState<File | null>(null);
  const [busy, setBusy] = useState<"install" | "revert" | null>(null);
  const [failure, setFailure] = useState<{
    title: string;
    messages: readonly string[];
  } | null>(null);

  async function install() {
    if (!bundle || !css) return;
    setBusy("install");
    setFailure(null);
    try {
      const r = await installDesignSystem(deps(siteId), {
        bundle: await bundle.text(),
        css: await css.text(),
      });
      if (r.ok) {
        setBundle(null);
        setCss(null);
        reload();
      } else setFailure({ title: STAGE[r.stage], messages: r.messages });
    } finally {
      setBusy(null);
    }
  }

  async function revert() {
    setBusy("revert");
    setFailure(null);
    try {
      await deps(siteId).server.setActive(null);
      reload();
    } catch (e) {
      setFailure({
        title: "Couldn't go back to Duro",
        messages: [e instanceof Error ? e.message : String(e)],
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text variant="label">Content design system</Text>
        {state.kind === "loading" && (
          <Inline gap="xs">
            <Spinner size="sm" />
            <Text variant="bodySm" color="muted">
              Loading…
            </Text>
          </Inline>
        )}
        {state.kind === "duro" && <Text variant="bodySm">Duro (built in)</Text>}
        {state.kind === "custom" && (
          <Text variant="bodySm">
            {state.manifest.name} — installed{" "}
            {new Date(state.manifest.uploadedAt).toLocaleDateString()}
          </Text>
        )}
        {state.kind === "unavailable" && (
          <Alert variant="error">
            The site's design system couldn't be loaded: {state.reason}.
            Publishing and previewing are blocked until it can.
          </Alert>
        )}
        <Text variant="caption" color="muted">
          How the site's buttons, headings, cards and inputs render. The editor
          itself always uses Duro.
        </Text>
      </Stack>

      {state.kind === "needs-consent" && (
        <Callout variant="warning" align="start">
          <Stack gap="sm" align="start">
            <Text variant="bodySm">
              This site uses the design system “{state.manifest.name}”, uploaded
              by its owner. Its code runs isolated on this device, with no
              access to your account or the network. Allow it to see the site as
              published, and to preview or publish it.
            </Text>
            <Button variant="primary" onClick={allow}>
              Allow on this device
            </Button>
          </Stack>
        </Callout>
      )}

      {isOwner ? (
        <Stack gap="sm" align="start">
          <Text variant="label">Install a design system</Text>
          <Text variant="caption" color="muted">
            One ES module (react, react/jsx-runtime and react-dom left external)
            and one stylesheet. It's checked before it replaces the current one.
          </Text>
          <Inline gap="sm">
            <Button
              onClick={() =>
                void pickFile(".js,.mjs,text/javascript").then(
                  (f) => f && setBundle(f),
                )
              }
              disabled={busy !== null}
            >
              {bundle ? bundle.name : "Choose bundle (.js)"}
            </Button>
            <Button
              onClick={() =>
                void pickFile(".css,text/css").then((f) => f && setCss(f))
              }
              disabled={busy !== null}
            >
              {css ? css.name : "Choose stylesheet (.css)"}
            </Button>
          </Inline>
          <Inline gap="sm">
            <Button
              variant="primary"
              onClick={() => void install()}
              disabled={!bundle || !css || busy !== null}
            >
              {busy === "install" ? "Checking…" : "Install"}
            </Button>
            {state.kind !== "duro" && state.kind !== "loading" && (
              <Button onClick={() => void revert()} disabled={busy !== null}>
                {busy === "revert" ? "Reverting…" : "Back to Duro"}
              </Button>
            )}
          </Inline>
          {failure && (
            <Alert variant="error">
              <Stack gap="xs">
                <Text variant="bodySm">{failure.title}:</Text>
                {failure.messages.map((m, i) => (
                  <Text key={i} variant="bodySm">
                    • {m}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      ) : (
        <Text variant="bodySm" color="muted">
          Only the site's owner can change its design system.
        </Text>
      )}
    </Stack>
  );
}

/** The tab in its own drawer, opened from the editor header. */
export function DesignSystemDrawer({
  onClose,
  ...props
}: Props & { readonly onClose: () => void }) {
  return (
    <Drawer.Root
      open
      anchor="right"
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Drawer.Portal size="md">
        <Drawer.Header>
          <Drawer.Title>Design system</Drawer.Title>
          <Drawer.Close />
        </Drawer.Header>
        <Drawer.Body>
          <DesignSystemTab {...props} />
        </Drawer.Body>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
